import Cookies from "js-cookie";

// Shared parent domain for every SSO handoff cookie the marketing site writes
// before redirecting here (submission_page, UTM, Varify, Gravity).
export const SSO_HANDOFF_COOKIE_DOMAIN = ".tigerdata.com";

// Written by the marketing site's SSO button with this form page's own live
// experiment; consumed (and cleared) on read by getVarifyFields.
export const VARIFY_FORM_PAGE_HANDOFF_COOKIE_NAME = "varify_form_page_handoff";

type VarifyExperiment = { experimentSlug: string; variantId: string };
type VarifyExperiments = Record<string, VarifyExperiment>;

// Cap so a tampered .tigerdata.com cookie can't inflate the request header.
const MAX_VARIFY_ENTRIES = 50;

// Parses one Varify cookie into a validated experiments object, dropping
// malformed entries. Validating before the merge keeps a corrupt entry in one
// cookie from clobbering a good one at the same id in the other.
const parseVarifyCookie = (raw: string | undefined): VarifyExperiments => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return Object.entries(parsed as Record<string, unknown>)
      .slice(0, MAX_VARIFY_ENTRIES)
      .reduce<VarifyExperiments>((acc, [id, entry]) => {
        if (
          entry &&
          typeof entry === "object" &&
          typeof (entry as { experimentSlug?: unknown }).experimentSlug ===
            "string"
        ) {
          const { experimentSlug, variantId } = entry as {
            experimentSlug: string;
            variantId?: unknown;
          };
          acc[id] = { experimentSlug, variantId: String(variantId ?? "") };
        }
        return acc;
      }, {});
  } catch {
    return {};
  }
};

// Builds the three Varify HubSpot fields, matching the website's
// getVarifyFieldsForSubmission (live form-page value ∪ flowthrough). Attribution
// arrives via two shared cookies: SPZExperimentData (flowthrough, left intact)
// and varify_form_page_handoff (this form's own value, consumed on read).
// flowthrough wins a collision. Returns null when there's nothing, so the
// call-site spread adds no empty varify_* fields.
export const getVarifyFields = () => {
  const flowthrough = parseVarifyCookie(Cookies.get("SPZExperimentData"));

  const handoffRaw = Cookies.get(VARIFY_FORM_PAGE_HANDOFF_COOKIE_NAME);
  if (handoffRaw !== undefined) {
    Cookies.remove(VARIFY_FORM_PAGE_HANDOFF_COOKIE_NAME, {
      domain: SSO_HANDOFF_COOKIE_DOMAIN,
    });
  }
  const formPageOwn = parseVarifyCookie(handoffRaw);

  const entries = Object.entries({ ...formPageOwn, ...flowthrough });
  if (entries.length === 0) return null;

  return {
    varify_experiment_id: entries.map(([id]) => id).join(","),
    varify_experiment_slug: entries.map(([, e]) => e.experimentSlug).join(","),
    varify_variant_id: entries.map(([, e]) => e.variantId).join(","),
  };
};

// Written by the marketing site's SSO button with captured Gravity
// attribution; consumed (and cleared) on read by getGravityFields.
export const GRAVITY_FORM_PAGE_HANDOFF_COOKIE_NAME =
  "gravity_form_page_handoff";

type GravityAttribution = {
  click_id: string;
  visitor_id: string | null;
  session_id: string | null;
};

// The cookie is client-set and unauthenticated (it can't be HttpOnly — it's
// written from JS), so this is a sanity ceiling, not a verified Gravity id
// length.
const MAX_GRAVITY_FIELD_LENGTH = 256;

// A string field within the length cap, or null (absent, wrong type, or
// oversized).
const cappedString = (value: unknown): string | null =>
  typeof value === "string" && value.length <= MAX_GRAVITY_FIELD_LENGTH
    ? value
    : null;

// Parses the Gravity handoff cookie, dropping anything that doesn't match
// the marketing site's shape — including a missing click_id or a field that
// exceeds the length cap.
const parseGravityCookie = (
  raw: string | undefined,
): GravityAttribution | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const { click_id, visitor_id, session_id } = parsed as Record<
      string,
      unknown
    >;

    const validClickId = cappedString(click_id);
    if (!validClickId) return null;

    // An optional field that's a string but too long invalidates the whole
    // cookie; one that's simply absent or the wrong type just becomes null.
    if (typeof visitor_id === "string" && cappedString(visitor_id) === null) {
      return null;
    }
    if (typeof session_id === "string" && cappedString(session_id) === null) {
      return null;
    }

    return {
      click_id: validClickId,
      visitor_id: cappedString(visitor_id),
      session_id: cappedString(session_id),
    };
  } catch {
    return null;
  }
};

// Builds the three Gravity HubSpot fields consumed by
// /api/gravity-conversion. Consumed on read, matching getVarifyFields; null
// when there's nothing to add.
export const getGravityFields = () => {
  const handoffRaw = Cookies.get(GRAVITY_FORM_PAGE_HANDOFF_COOKIE_NAME);
  if (handoffRaw !== undefined) {
    Cookies.remove(GRAVITY_FORM_PAGE_HANDOFF_COOKIE_NAME, {
      domain: SSO_HANDOFF_COOKIE_DOMAIN,
    });
  }

  const attribution = parseGravityCookie(handoffRaw);
  if (!attribution) return null;

  return {
    gravity_click_id: attribution.click_id,
    gravity_visitor_id: attribution.visitor_id || "",
    gravity_session_id: attribution.session_id || "",
  };
};
